import React from 'react';
import { observer } from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { FormField } from '../../../global/form/form-field/form-field';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { ONLY_SPACES_REG_EXP } from '../../../../constants/regex.constants';
import { MORE_BUTTON_TEXT, submitText } from '../../../../constants/common-labels.constants';
import { HubOrganizationFormVM } from './hub-organization-form.viewmodel';
import * as mobx from 'mobx';
import OptionsButton from '../../../global/options-button/options-button';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';

const HubOrganizationFormPresentation = injectIntl(observer((props: { vm: HubOrganizationFormVM; intl: IntlShape }) => {
    const { vm } = props;
    const formData = vm.getOrganizationFormDataVM();
    const isEditMode = vm.getIsEditMode();
    const canDeleteHubOrganization = hasPermission(Permission.DELETE_HUB_ORGANIZATION);
    const hubOrganizationDeleteOptions = [
        {
            label: props.intl.formatMessage({
                id: 'oe.organization.deleteOrganization',
                defaultMessage: 'Delete Organization',
                description: 'Delete hub organization option',
            }),
            action: vm.deleteOrganization,
            danger: true,
        },
    ];

    const removeOnlySpacesFromField = mobx.action((value: string, setter: (val: string) => void) => {
        if (!(value.replace(ONLY_SPACES_REG_EXP, '').length)) {
            setter('');
        }
    });

    return (
        <CommonPopupBodyFooterLayout
            body={
                <form className='oe-form'>
                    <div className='oe-form__body'>
                        <FormField label={<FormattedMessage
                            defaultMessage='Organization'
                            id='oe.event.organization'
                            description='Event Form label'
                        />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.name || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.setName(value);
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, val => formData.setName(val));
                                })}
                                autoFocus
                            />
                            {(formData.formChecked || formData.hasApiError('name')) && formData.getNameError() &&
                                <span className='oe-form-field__error'>{formData.getNameError()}</span>}
                            {formData.getServerErrorMessage() && <div className='oe-form-field__error'>{formData.getServerErrorMessage()}</div>}
                        </FormField>
                        <FormField label={<FormattedMessage
                            defaultMessage='Type'
                            id='oe.event.type'
                            description='Event Form label'
                        />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.organizationType || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.setType(value);
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, val => formData.setType(val));
                                })}
                            />
                            {(formData.formChecked || formData.hasApiError('organizationType')) && formData.getFieldError('organizationType') &&
                                <span className='oe-form-field__error'>{formData.getFieldError('organizationType')}</span>}
                        </FormField>
                        <FormField label={<FormattedMessage
                            defaultMessage='Ticker'
                            id='oe.event.ticker'
                            description='Event Form label'
                        />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.organizationTicker || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.setTicker(value);
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, val => formData.setTicker(val));
                                })}
                            />
                            {(formData.formChecked || formData.hasApiError('organizationTicker')) && formData.getFieldError('organizationTicker') &&
                                <span className='oe-form-field__error'>{formData.getFieldError('organizationTicker')}</span>}
                        </FormField>
                        <FormField label={<FormattedMessage
                            defaultMessage='External Organization ID'
                            id='oe.event.externalOrganizationId'
                            description='Event Form label'
                        />}>
                            <input
                                className='oe-form-field__control'
                                value={formData.externalOrganizationId || ''}
                                onChange={mobx.action(({ target: { value } }) => {
                                    formData.setExternalOrganizationId(value);
                                })}
                                onBlur={mobx.action(({ target: { value } }) => {
                                    removeOnlySpacesFromField(value, val => formData.setExternalOrganizationId(val));
                                })}
                            />
                            {(formData.formChecked || formData.hasApiError('externalOrganizationId')) && formData.getFieldError('externalOrganizationId') &&
                                <span className='oe-form-field__error'>
                                    {formData.getFieldError('externalOrganizationId')}
                                </span>}
                        </FormField>
                    </div>
                </form>
            }
            footer={
                <>
                    {vm.isSaving && (
                        <span className='oe-popup-layout__footer-status-area'>
                            <FormattedMessage
                                id='oe.organization.organizationDataIsLoading'
                                description='Preloader message text'
                                defaultMessage='Organization data is loading, please wait{suffix}'
                                values={{
                                    suffix: '...'
                                }}
                            />
                        </span>
                    )}
                    <div className='buttons-block buttons-block--from-right'>
                        <DefaultCloseSubWindowButton
                            text={submitText(isEditMode)}
                            modifier={['fat', 'confirm-safe']}
                            onClick={vm.formSubmitAction}
                            disabled={vm.isSaving}
                        />
                        <DefaultCloseSubWindowButton text='Cancel' />
                        {isEditMode && canDeleteHubOrganization && (
                            <OptionsButton
                                className='options-button--right-margin-auto'
                                modifier={['fat', 'options']}
                                label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                                options={hubOrganizationDeleteOptions}
                                position='top'
                                align='start'
                            />
                        )}
                    </div>
                </>
            }
        />
    );
}));

export { HubOrganizationFormPresentation };

